import { createFileRoute, Link } from "@tanstack/react-router";
import { storiesWithSlugs } from "@/lib/results-stories";
import { storyCardAlt } from "@/lib/story-titles";

const SITE = "https://virginiabariatric.com";

export const Route = createFileRoute("/results/story-preview")({
  component: StoryPreviewPage,
  head: () => ({
    meta: [
      { title: "Story Share-Card Preview | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Internal preview of the Open Graph, Twitter Card, canonical and breadcrumb metadata generated for every before-and-after story page.",
      },
      { name: "robots", content: "noindex, nofollow" },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Story Share-Card Preview | Virginia Bariatric" },
        { property: "og:description", content: "Internal preview of the Open Graph, Twitter Card, canonical and breadcrumb metadata generated for every before-and-after story page." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `${SITE}/results/story-preview` },
        { name: "twitter:card", content: "summary_large_image" },
      ] : []),
    ],
    links: [{ rel: "canonical", href: `${SITE}/results/story-preview` }],
  }),
});

function StoryPreviewPage() {
  const stories = storiesWithSlugs();

  return (
    <main className="container mx-auto max-w-5xl px-4 py-12">
      <header className="mb-10 text-center">
        <h1 className="text-3xl font-bold text-brand-logo md:text-4xl">
          Story Share-Card Preview
        </h1>
        <p className="mt-3 text-muted-foreground">
          {stories.length} before-and-after story pages, each with its own Open Graph image,
          Twitter Card, self-referencing canonical URL and BreadcrumbList schema. Run{" "}
          <code className="rounded bg-muted px-1.5 py-0.5 text-sm">
            node scripts/story-meta-check.mjs
          </code>{" "}
          to re-verify.
        </p>
      </header>

      <ul className="grid gap-8 sm:grid-cols-2">
        {stories.map(({ slug, story }) => {
          const url = `/results/story/${slug}`;
          return (
            <li
              key={slug}
              className="overflow-hidden rounded-lg border border-border bg-card shadow-sm"
            >
              <img
                src={`/og/stories/${slug}.jpg`}
                alt={storyCardAlt(story.label, "Social share card")}
                width={1200}
                height={630}
                loading="lazy"
                decoding="async"
                className="w-full"
              />
              <div className="space-y-2 p-4 text-sm">
                <h2 className="text-base font-semibold text-foreground">{story.label}</h2>
                <p className="text-muted-foreground">{story.caption}</p>
                <dl className="space-y-1 text-xs text-muted-foreground">
                  <div>
                    <dt className="inline font-medium text-foreground">canonical: </dt>
                    <dd className="inline break-all">{`${SITE}${url}`}</dd>
                  </div>
                  <div>
                    <dt className="inline font-medium text-foreground">og:image: </dt>
                    <dd className="inline break-all">{`${SITE}/og/stories/${slug}.jpg`}</dd>
                  </div>
                  <div>
                    <dt className="inline font-medium text-foreground">breadcrumb: </dt>
                    <dd className="inline">Home › Before &amp; After › {story.label}</dd>
                  </div>
                </dl>
                <Link to="/results/story/$slug" params={{ slug }} className="inline-block font-medium text-brand-logo underline">
                  Open story page
                </Link>
              </div>
            </li>
          );
        })}
      </ul>
    </main>
  );
}
